import { getBackendURL } from "../components/env";
import { transitionTask } from "./messagingService";
import { transitionOrderItem, unassignOrderItem } from "./clinicComplianceService";
import { staffRequest } from "./staffRequest";
import { emitWorkItemsChanged } from "../utils/workItemsBus";
import { refOf } from "../utils/workItemRefs";

// backend/api/work_items.py: a task's or order's card, activity feed,
// comments and reassignment. Every call sends the signed-in user's token, with
// one refresh-and-retry after a 401 (staffRequest), and the server takes the
// author and the actor from it: the parameters that name them (actorId,
// authorId) are kept so that callers need not change, and are never sent.
function workItemsRequest(path, { body, params } = {}) {
  const url = new URL(`${getBackendURL()}/api/work-items${path}`);
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value != null && value !== "") url.searchParams.set(key, value);
  });
  return staffRequest(url.toString(), {
    method: body ? "POST" : "GET",
    body,
    failure: "Work items API error",
  });
}

// kind: "task" | "order". Returns { newStatus } (raw DB value). `message`
// (reject, cancel, unassign) goes into the item's activity feed; `to` is the
// board column a "move" (a drag on the Tasks board) goes to.
export async function transitionWorkItem({ kind, id, schema }, action, actorId, { message, to } = {}) {
  let newStatus;
  const extra = { ...(message ? { message } : {}), ...(to ? { to } : {}) };
  if (kind === "task") {
    const res = await (Object.keys(extra).length
      ? transitionTask(id, action, actorId, extra)
      : transitionTask(id, action, actorId));
    newStatus = res?.task?.status;
  } else if (action === "unassign") {
    await unassignOrderItem(id, { schema, actorId, ...(message ? { message } : {}) });
    newStatus = "unassigned";
  } else {
    const res = await transitionOrderItem(id, action, { actorId, schema, ...extra });
    newStatus = res?.item_status;
  }
  emitWorkItemsChanged({ kind, id, action });
  return { newStatus };
}

// Hand a task or order to someone else. Returns { status, item }.
export async function reassignWorkItem(item, _actorId, toUserId, message) {
  const res = await workItemsRequest("/reassign/", {
    body: { ...refOf(item), to_user_id: toUserId, ...(message ? { message } : {}) },
  });
  emitWorkItemsChanged({ kind: item.kind, id: item.id, action: "reassign" });
  return res;
}

// { item }: what a link's card shows.
export const fetchWorkItem = (item) => workItemsRequest("/item/", { params: refOf(item) });

// { activity, ready }: the timeline and comments, oldest first.
export const fetchWorkItemActivity = (item) => workItemsRequest("/activity/", { params: refOf(item) });

export async function commentOnWorkItem(item, _authorId, body) {
  const res = await workItemsRequest("/comment/", { body: { ...refOf(item), body } });
  emitWorkItemsChanged({ kind: item.kind, id: item.id, action: "comment" });
  return res;
}

// { summaries: { [summaryKey]: { comments, rejection } } } for the items that have any.
export const fetchWorkItemSummaries = (items) =>
  workItemsRequest("/summaries/", { body: { items: (items || []).map(refOf) } });

export function transitionErrorMessage(err) {
  return err?.message || "Something went wrong";
}
